/**
 * Hook for global marketplace metrics (not user-specific).
 * Fetches counts of active negotiations, active listings, and completed sales.
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

interface MarketplaceGlobalMetrics {
  activeNegotiations: number;
  activeListings: number;
  completedNegotiations: number;
  lastNegotiationAt: string | null;
  lastSaleAt: string | null;
}

async function fetchGlobalMetrics(): Promise<MarketplaceGlobalMetrics> {
  const { data, error } = await supabase.rpc('get_marketplace_global_counts');

  if (error) {
    console.error('Error fetching marketplace global counts:', error);
    return { activeNegotiations: 0, activeListings: 0, completedNegotiations: 0, lastNegotiationAt: null, lastSaleAt: null };
  }

  const parsed = data as Record<string, unknown> | null;

  return {
    activeNegotiations: (parsed?.active_negotiations as number) ?? 0,
    activeListings: (parsed?.active_listings as number) ?? 0,
    completedNegotiations: (parsed?.completed_negotiations as number) ?? 0,
    lastNegotiationAt: (parsed?.last_negotiation_started_at as string) ?? null,
    lastSaleAt: (parsed?.last_sale_completed_at as string) ?? null,
  };
}

export function useMarketplaceGlobalMetrics() {
  const query = useQuery({
    queryKey: ['marketplace-global-metrics'],
    queryFn: fetchGlobalMetrics,
    staleTime: 2 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    metrics: query.data ?? { activeNegotiations: 0, activeListings: 0, completedNegotiations: 0, lastNegotiationAt: null, lastSaleAt: null },
    isLoading: query.isLoading,
  };
}
